import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Calendar, CheckCircle2, FileText, Users } from "lucide-react";

import { useDelayedFlag, useTran } from "@/hooks";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "@/lib/utils";

import { useEsgDashboardStore } from "../../hooks";

const CompletionTracker = observer(() => {
  const { t } = useTran();
  const { frameworkCompletion, completionSummary, loading, ensureData } =
    useEsgDashboardStore();
  const showSkeleton = useDelayedFlag(loading);

  useEffect(() => {
    ensureData();
  }, [ensureData]);

  const progressColor = (percentage) => {
    if (percentage >= 90) return "bg-emerald-500";
    if (percentage >= 70) return "bg-blue-500";
    if (percentage >= 50) return "bg-amber-500";
    return "bg-rose-500";
  };

  const statusBadge = (percentage) => {
    if (percentage >= 90) {
      return (
        <Badge className="bg-emerald-500 text-white">{t("On Track")}</Badge>
      );
    }
    if (percentage >= 70) {
      return (
        <Badge className="bg-blue-500 text-white">{t("In Progress")}</Badge>
      );
    }
    if (percentage >= 50) {
      return (
        <Badge className="bg-amber-500 text-white">
          {t("Needs Attention")}
        </Badge>
      );
    }
    return <Badge className="bg-rose-500 text-white">{t("At Risk")}</Badge>;
  };

  if (showSkeleton) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="border-b border-border">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="mt-2 h-3 w-56" />
        </CardHeader>
        <CardContent className="space-y-4 p-6">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton
              key={`framework-skeleton-${index}`}
              className="h-20 w-full"
            />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b border-border bg-muted/30 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-foreground">
              {t("Framework Completion Status")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("Data entry progress across frameworks")}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-primary">
              {completionSummary.overallPercentage}%
            </p>
            <p className="text-xs text-muted-foreground">{t("Overall")}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {frameworkCompletion.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            {t("No framework completion data available yet.")}
          </div>
        ) : (
          <div className="space-y-4">
            {frameworkCompletion.map((framework) => (
              <div
                key={framework.framework}
                className="rounded-radius border border-border bg-card p-4"
              >
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <p className="font-semibold text-foreground">
                        {framework.framework}
                      </p>
                      {statusBadge(framework.completionPercentage)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {framework.frameworkName}
                    </p>
                  </div>
                  <div className="text-right text-lg font-semibold text-primary">
                    {framework.completionPercentage}%
                  </div>
                </div>

                <div className="mb-3 h-2 rounded-full bg-muted">
                  <div
                    className={`h-2 rounded-full ${progressColor(
                      framework.completionPercentage,
                    )}`}
                    style={{ width: `${framework.completionPercentage}%` }}
                  />
                </div>

                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="rounded bg-emerald-500/10 p-2">
                    <div className="flex items-center justify-center gap-1 text-emerald-500">
                      <CheckCircle2 className="h-3 w-3" />
                      <span className="font-bold">
                        {framework.completedMetrics}
                      </span>
                    </div>
                    <p className="text-muted-foreground">{t("Complete")}</p>
                  </div>
                  <div className="rounded bg-amber-500/10 p-2">
                    <div className="flex items-center justify-center gap-1 text-amber-500">
                      <FileText className="h-3 w-3" />
                      <span className="font-bold">
                        {framework.pendingMetrics}
                      </span>
                    </div>
                    <p className="text-muted-foreground">{t("Pending")}</p>
                  </div>
                  <div className="rounded bg-muted/40 p-2">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground">
                      <FileText className="h-3 w-3" />
                      <span className="font-bold">
                        {framework.notStartedMetrics}
                      </span>
                    </div>
                    <p className="text-muted-foreground">{t("Not Started")}</p>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    <span>{framework.assignedTo}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>
                      {t("Due")}:{" "}
                      {formatDateTime(framework.dueDate, {
                        dateStyle: "medium",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {frameworkCompletion.length > 0 ? (
          <div className="mt-4 rounded-radius bg-primary/10 p-3 text-sm text-primary">
            <div className="flex items-center justify-between">
              <span>{t("Total Metrics Across Frameworks")}:</span>
              <span className="font-semibold">
                {completionSummary.completedMetrics} /{" "}
                {completionSummary.totalMetrics}
              </span>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
});

export default CompletionTracker;
